import {Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {get_company_event_types} from "../../graphql/getCompanyEvents";
import {Apollo} from "apollo-angular";
import {get_states} from "../../../graphql/state";
import {
  create_address_for_company_event, delete_address,
  get_zipcode, update_address_for_company_event
} from "../../../deals/deals-subcomponents/addresses/graphql/alterAddresses";
import {AlertService} from "../../../alert/alert.service";
import {ActivatedRoute} from "@angular/router";
import {DateTimeService} from "../../../services/date-time.service";;
import {DatePipe} from "@angular/common";

@Component({
    selector: 'company-events-information',
    templateUrl: './company-events-information.component.html',
    styleUrls: ['./company-events-information.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanyEventsInformationComponent implements OnInit{
  @Input() event: any;
  @Input() isOnUserCalendar: boolean;
  @Output() onEventToolbarAction = new EventEmitter()
  @Output() updateGeneralInfo = new EventEmitter()
  @Input() permissions:any;
  isEditMode: boolean;

  startDate: any = null;
  endDate: any = null;
  startTime: any = null;
  endTime: any = null;
  addresses:any = []
  activeEventDate:any = null
  inactiveEventDate:any = null
  editEventForm: FormGroup;
  addressForm: FormGroup;
  selectEventTypes:any = []
  selectStates:any = []
  addressToggle:boolean = false;
  isEditingAddress:boolean = false
  selectedAddress:any;
  timezones:any = [];
  companyEventId: any;
  zipCodeId:any;
  timezone:any = null;


  constructor(
    private apollo: Apollo,
    private route: ActivatedRoute,
    private alertService: AlertService,
    public dateTimeService: DateTimeService,
    private datePipe: DatePipe
  ) {
  }

  ngOnInit() {
    this.companyEventId = this.route.snapshot.paramMap.get('id')

    if(this.event.is_postponed){
      this.activeEventDate = this.event.companyEventDates.find((date:any) => date.initial_date_time==1) ?? null
      this.inactiveEventDate = this.event.companyEventDates.find((date:any) => date.initial_date_time==0) ?? null;
    } else {
      this.activeEventDate = this.event.companyEventDates[0]
    }
    let formattedDates = this.dateTimeService.formatCompanyEventTimes(this.activeEventDate == null && this.event.is_postponed ? this.inactiveEventDate : this.activeEventDate)

    this.startDate = formattedDates.startDate;
    this.endDate = formattedDates.endDate==formattedDates.startDate ? null : formattedDates.endDate;
    this.startTime = formattedDates.viewStart;
    this.endTime = formattedDates.viewEnd;

    this.addresses = this.event.companyEventAddresses;


    if(this.addresses.length>0){
      this.addressToggle = true;
    }
    /*
   --------------- Converts date to prevent form field from prepopulating 1 day behind------------
     */
    let convertDate = (string: string) => {
      let d = new Date(string)
      d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
      return d
    }

    /*
    ------------General and Time information form------------------------
     */
    this.editEventForm = new FormGroup<any>({
      name: new FormControl(this.event.title, Validators.required),
      type: new FormControl(this.event.companyEventType.id, Validators.required),
      typeName: new FormControl(this.event.companyEventType.name),
      startDate: new FormControl(convertDate(this.startDate), Validators.required),
      endDate: new FormControl(this.endDate==null || this.endDate === this.startDate ? null : convertDate(this.endDate)),
      startTime: new FormControl(formattedDates.viewStart),
      endTime: new FormControl(formattedDates.viewEnd),
      is_sponsored: new FormControl(this.event.is_sponsored),
      sponsorshipAmount: new FormControl(this.event.sponsorship_amount),
      is_cosponsored: new FormControl(this.event.is_cosponsored),
      is_postponed: new FormControl(this.event.is_postponed),
      is_cancelled: new FormControl(this.event.is_cancelled),
      cosponsor: new FormControl(this.event.cosponsor),
      notes: new FormControl(this.event.notes),
      timezone: new FormControl(formattedDates.timezone)
    })

    this.timezones = this.dateTimeService.timezones;
    this.timezone = this.timezones.find((zone:any) => zone.tz == formattedDates.timezone);
    if(this.event.is_postponed){
      this.addPostponeFormControls()
      this.setPostponeFields(convertDate)
    }

    /*
    --------------Address form------------------
     */
    this.addressForm = new FormGroup<any>({
      name: new FormControl(null),
      address1: new FormControl(null, Validators.required),
      address2: new FormControl(null,),
      city: new FormControl(null, Validators.required),
      state: new FormControl(null, Validators.required),
      zipCode: new FormControl(null, [Validators.required, Validators.pattern("^[0-9]*$"), Validators.min(5)])
    })


  }

  toolbarAction(event:any){
    this.onEventToolbarAction.emit(event)
  }

  togglePostpone(event:any){
    let is_postponed:boolean = this.editEventForm.controls['is_postponed'].value;
    if(is_postponed){
      this.editEventForm.controls['is_postponed'].setValue(false)
      this.editEventForm.controls['is_postponed'].markAsTouched()
    }
    if(!is_postponed){
      this.editEventForm.controls['is_cancelled'].setValue(false);
      this.editEventForm.controls['is_postponed'].setValue(true);
      this.editEventForm.controls['is_postponed'].markAsTouched()
      this.addPostponeFormControls();
    }

  }

  toggleCancelled(event:any){
    let is_cancelled:boolean = this.editEventForm.controls['is_cancelled'].value;
    if(is_cancelled){
      this.editEventForm.controls['is_cancelled'].setValue(false);
      this.editEventForm.controls['is_cancelled'].markAsTouched()
    }
    if(!is_cancelled){
      this.editEventForm.controls['is_postponed'].setValue(false);
      this.editEventForm.controls['is_cancelled'].setValue(true);
      this.editEventForm.controls['is_cancelled'].markAsTouched()
    }
  }

  openAddressScreen(){
    this.addressToggle = !this.addressToggle;
  }

  addPostponeFormControls(){
    this.editEventForm.addControl('postponeStartDate', new FormControl(null, Validators.required));
    this.editEventForm.addControl('postponeEndDate', new FormControl(null));
    this.editEventForm.addControl('postponeStartTime', new FormControl(null));
    this.editEventForm.addControl('postponeEndTime', new FormControl(null));
    this.editEventForm.addControl('postponeTimezone', new FormControl(this.timezone))
  }

  setPostponeFields(convertCall:(d:any) => any){
    let hasMultiDates = this.activeEventDate!==null && this.inactiveEventDate!==null;
    let activeFormatted = this.activeEventDate !== null ? this.dateTimeService.formatCompanyEventTimes(this.activeEventDate) : null
    this.editEventForm.controls['postponeStartDate'].setValue(hasMultiDates ? convertCall(this.activeEventDate?.start_date) : null)
    this.editEventForm.controls['postponeEndDate'].setValue(hasMultiDates ? this.activeEventDate?.end_date==null || this.activeEventDate?.end_date == this.activeEventDate?.start_date ? null : convertCall(this.activeEventDate?.end_date) : null)
    this.editEventForm.controls['postponeStartTime'].setValue(hasMultiDates ? activeFormatted?.viewStart : null)
    this.editEventForm.controls['postponeEndTime'].setValue(hasMultiDates ? activeFormatted?.viewEnd : null)
    this.editEventForm.controls['postponeTimezone'].setValue(hasMultiDates ? activeFormatted?.timezone : null)
    if(this.inactiveEventDate!==null){
      let inactiveFormatted = this.dateTimeService.formatCompanyEventTimes(this.inactiveEventDate)
      this.editEventForm.controls['startDate'].setValue(convertCall(this.inactiveEventDate.start_date))
      this.editEventForm.controls['endDate'].setValue(this.inactiveEventDate.end_date===null || this.inactiveEventDate.end_date == this.inactiveEventDate.start_date ? null : convertCall(this.inactiveEventDate.end_date))
      this.editEventForm.controls['startTime'].setValue(inactiveFormatted.viewStart)
      this.editEventForm.controls['endTime'].setValue(inactiveFormatted.viewEnd)
      this.editEventForm.controls['timezone'].setValue(inactiveFormatted.timezone);
    }
  }

  saveAddress(){
    this.searchZipCode(
      (result) => {
        let payload = {
          address1: this.addressForm.controls['address1'].value,
          address2: this.addressForm.controls['address2'].value,
          zip_code_id: result,
          company_event_addresses: {
            create: this.isEditingAddress ? [] : [{name: this.addressForm.controls['name'].value, company_event_id: this.companyEventId}],
            update: this.isEditingAddress ? [{name: this.addressForm.controls['name'].value, id: this.selectedAddress.id}] : []
          }
        };
        let query = this.isEditingAddress ? update_address_for_company_event : create_address_for_company_event
        let createVar = { input: payload }
        let updateVar = { input: payload, id: this.isEditingAddress ? this.selectedAddress.address_id : null}

        this.apollo
          .mutate({
            mutation: query,
            variables: this.isEditingAddress ? updateVar : createVar
          })
          .subscribe(({ data }) => {
            let response:any = data;
            let newAddress = response.createAddressForCompanyEvent;
            let updateAddress = response.updateAddressForCompanyEvent;
            if(newAddress!==null && newAddress!==undefined){
              let newAddrPayload = {address: newAddress, name: newAddress.company_event_addresses[0].name, address_id: newAddress.id, id: newAddress.company_event_addresses[0].id}
              this.addresses = [...this.addresses, newAddrPayload]
            }
            if(updateAddress!==null && updateAddress!==undefined){
              const updatedAddresses = this.addresses.map((addr: any) => {
                if(addr.address_id==updateAddress.id){
                  return {address: updateAddress, name: updateAddress.company_event_addresses[0].name, address_id: updateAddress.id}
                }
                return addr;
              })
              this.addresses = updatedAddresses;
              this.isEditingAddress = false;
            }
            this.addressForm.reset()
          });
      },
      (err) => {
        this.alertService.add('', err.msg)
      }
    )
  }

  searchZipCode(subscription:(data:any)=>any, error:(err:any)=>any){
    let dataSubscription = this.apollo
      .watchQuery<any>({
        query: get_zipcode,
        variables: {
          zipcode: this.addressForm.controls['zipCode'].value,
          city: this.addressForm.controls['city'].value,
          state: this.addressForm.controls['state'].value.id,
          county: {id: null, name: null},
        },
      })
      .valueChanges.subscribe(({ data }) => {
      let foundZipCode = true;
      if (data.zipCodeWithCityStateAndCounty !== null) {
        this.zipCodeId = data.zipCodeWithCityStateAndCounty.id;
      } else if (
        data.zipCodeWithCityState !== null &&
        data.zipCodeWithCityState.length > 0
      ) {
        this.zipCodeId = data.zipCodeWithCityState[0].id;
      } else if (
        data.zipCodeWithCity &&
        data.zipCodeWithCity.length > 0
      ) {
        this.zipCodeId = data.zipCodeWithCity[0].id;
      } else if (data.zipCode && data.zipCode.length > 0) {
        this.zipCodeId = data.zipCode[0].id;
      } else if (
        data.noZipCityState &&
        data.noZipCityState.length > 0
      ) {
        this.zipCodeId = data.noZipCityState[0].id;
      } else {
        foundZipCode = false;
      }
      if (foundZipCode) {
        subscription(this.zipCodeId)
      } else {
        error({msg: 'Failed to save address: Please enter a valid City/State or Zip Code and try again.'})
      }
      dataSubscription.unsubscribe()
    });
  }

  deleteAddressFromEvent(address:any){
    this.apollo.mutate({
      mutation: delete_address,
      variables: {
        id: address.address_id
      }
    }).subscribe(({data}) => {
      let response:any = data;
      let updatedAddresses = this.addresses.filter((adr:any) => adr.address_id!==response.deleteAddress.id)
      this.addresses = updatedAddresses;
    })
  }

  toggleAddressChange(address:any){
    this.isEditingAddress = !this.isEditingAddress;
    if(this.isEditingAddress){
      this.selectedAddress = address;
      let state = this.selectStates.find((state:any) => state.abbreviation == address.address.zipCode.state.abbreviation)
      this.addressForm.controls['name'].setValue(address.name);
      this.addressForm.controls['address1'].setValue(address.address.address1);
      this.addressForm.controls['address2'].setValue(address.address.address2);
      this.addressForm.controls['city'].setValue(address.address.zipCode.city);
      this.addressForm.controls['state'].setValue(state);
      this.addressForm.controls['zipCode'].setValue(address.address.zipCode.zipcode)
    } else {
      this.selectedAddress = null;
      this.addressForm.reset()
    }
  }

  radioChange(e:any, controlName: string){
    if(!e.value && controlName=='cosponsor'){
      this.editEventForm.controls['cosponsor'].reset()
    }
    if(!e.value && controlName=='sponsorship'){
      this.editEventForm.controls['sponsorshipAmount'].reset()
    }
  }

  onEventTypeSelectionChange(type:any){
    let nameValue = this.selectEventTypes.filter((eventType:any) => eventType.id==type.value)[0].name
    this.editEventForm.controls['typeName'].setValue(nameValue)
  }


  changeEditStatus(event:any){
    if(!this.isEditMode){
      let typesQuery = this.apollo.watchQuery({
        query: get_company_event_types,
      }).valueChanges.subscribe(({data}) => {
        let response:any = data;
        this.selectEventTypes = response.company_event_types;
        typesQuery.unsubscribe();
      })

      let statesQuery = this.apollo.watchQuery({
        query: get_states
      }).valueChanges.subscribe(({data}) => {
        let response:any = data;
        this.selectStates = response.states;
        statesQuery.unsubscribe();
      })
    }
    if(this.isEditMode && this.editEventForm.touched){
      let hasPostponeTimes = this.editEventForm.value.is_postponed && this.editEventForm.get('postponeStartDate')!==null && this.editEventForm.controls['postponeStartDate'].value!==null;
      this.startTime = !hasPostponeTimes ? this.editEventForm.controls['startTime'].value.length == 0 ? null : this.editEventForm.controls['startTime'].value : this.editEventForm.controls['postponeStartTime'].value?.length == 0 ? null : this.editEventForm.controls['postponeStartTime'].value;
      this.endTime = !hasPostponeTimes ? this.editEventForm.controls['endTime'].value.length == 0 ? null : this.editEventForm.controls['endTime'].value : this.editEventForm.controls['postponeEndTime'].value?.length == 0 ? null : this.editEventForm.controls['postponeEndTime'].value;
      this.startDate = !hasPostponeTimes ? this.datePipe.transform(this.editEventForm.controls['startDate'].value, "yyyy-MM-dd") : this.datePipe.transform(this.editEventForm.controls['postponeStartDate'].value, 'yyyy-MM-dd');
      this.endDate = !hasPostponeTimes ? this.datePipe.transform(this.editEventForm.controls['endDate'].value, "yyyy-MM-dd") : this.datePipe.transform(this.editEventForm.controls['postponeEndDate'].value, 'yyyy-MM-dd');
      this.timezone = !hasPostponeTimes ? this.timezones.find((tz: any) => tz.tz == this.editEventForm.controls['timezone'].value) : this.timezones.find((tz:any) => tz.tz == this.editEventForm.controls['postponeTimezone']?.value);
      if((!this.event.is_postponed && this.editEventForm.controls['is_postponed'].value) || this.event.is_postponed){
        let dateId = this.event.companyEventDates.find((date:any) => date.initial_date_time==1)?.id
        this.activeEventDate = !hasPostponeTimes ? null : this.dateTimeService.companyEventDateTimePayload(this.startDate, this.endDate, this.startTime, this.endTime, this.timezone?.tz, 1, dateId)
        this.inactiveEventDate = !hasPostponeTimes ?
          this.dateTimeService.companyEventDateTimePayload(this.startDate, this.endDate,
            this.startTime == null ? null : this.dateTimeService.convertTimezoneToUTC(new Date(this.startDate + " " + this.startTime), this.timezone?.tz),
            this.endTime == null ? null : this.dateTimeService.convertTimezoneToUTC(new Date(this.startDate + " " + this.endTime), this.timezone?.tz),
            this.timezone?.tz, 0, dateId) :
          this.dateTimeService.companyEventDateTimePayload(
            this.datePipe.transform(this.editEventForm.controls['startDate'].value, 'yyyy-MM-dd'),
            this.datePipe.transform(this.editEventForm.controls['endDate'].value, 'yyyy-MM-dd'),
            this.editEventForm.controls['startTime'].value.length==0 ? null : this.dateTimeService.convertTimezoneToUTC(new Date(this.datePipe.transform(this.editEventForm.value.startDate, 'yyyy-MM-dd') + " " + this.editEventForm.controls['startTime'].value), this.timezone?.tz),
            this.editEventForm.controls['endTime'].value.length==0 ? null : this.dateTimeService.convertTimezoneToUTC(new Date(this.datePipe.transform(this.editEventForm.value.startDate, 'yyyy-MM-dd') + " " + this.editEventForm.controls['endTime'].value), this.timezone?.tz),
            this.editEventForm.controls['timezone'].value,
            0,
            dateId
            )
      }
      this.updateGeneralInfo.emit(this.editEventForm);
    }
    this.isEditMode = !this.isEditMode
  }

}
